import { SchedulesV3GroupByType, ScheduleV3Filters, ScheduleV3Include } from '../model/scheduleV3';
import { IncludeDeptType } from '../../enums';

// API Request Parameters
export interface GetSchedulesV3Params {
  start_date: string;
  end_date: string;
  group_by: SchedulesV3GroupByType;
  filters?: ScheduleV3Filters;
  include?: Partial<ScheduleV3Include>;
  include_mydept?: IncludeDeptType;
}

// Sort options: employee view uses first_name_asc | first_name_desc | last_name_asc | last_name_desc; other views use asc | desc
export type ScheduleSidebarSortBy =
  | 'first_name_asc'
  | 'first_name_desc'
  | 'last_name_asc'
  | 'last_name_desc'
  | 'asc'
  | 'desc';

// Sidebar list (layout) params
export interface GetScheduleSidebarListParams {
  start_date: string;
  end_date: string;
  group_by: Extract<SchedulesV3GroupByType, 'employee' | 'department' | 'site' | 'tags'>;
  sort_by?: ScheduleSidebarSortBy;
  filters?: {
    department_codes?: string[];
    employee_ids?: string[];
    site_ids?: string[];
    tag_code?: string | null;
    group_code?: string[];
  };
  include_mydept?: IncludeDeptType;
}

// Add Schedule Request Parameters (V1)
export interface AddScheduleRequest {
  singleuser?: number;
  os_emp_account_id?: number[];
  os_schedules_site_shifts_id?: number;
  shift_code?: string;
  os_emp_start_datetime?: string;
  os_emp_end_datetime?: string;
  schedule_date?: string;
  title?: string;
  us_user_site_profile_id?: number;
  department_code?: string;
  os_work_codes_id?: number;
  shift_color?: string;
  logs?: string;
  occurence_type?: 'DAY' | 'WEEK';
  add_days?: number;
  start_from_date?: string;
  start_from_custom?: number;
  attendancetags?: {
    field_code: string;
    field_values: {
      value_code: string;
      value_text: string;
    }[];
  }[];
}

// Add Schedule Request Parameters (V2)
export interface AddScheduleV2Request {
  assign_to_employee_ids: string[];
  assign_to_group?: string[];
  assign_to_department?: string[];
  
  // Shift Selection
  shift_type: 'template' | 'free_flow' | 'notes';
  shift_code?: string; // Required if shift_type = template
  free_flow_shift?: { // Required if shift_type = free_flow
    start_datetime: string;
    end_datetime: string;
  };
  
  // Assignment Range
  repeat_type: 'DAY' | 'WEEK';
  repeat_count: number;
  start_date: string;
  days_of_week?: number[]; // Optional, for weekly repeat: [0=Sun .. 6=Sat]
  
  // Additional Fields
  shift_attributes?: {
    site_code?: string;
    department_code?: string;
    work_code?: string;
    notes?: string;
    logs?: string;
    shift_color?: string;
    attendance_tags?: Array<{
      code: string;
      value: string | string[];
    }>;
  };
}

// Update Schedule Request Parameters
export interface UpdateScheduleRequest {
  os_emp_schedules_id: number;
  os_emp_account_id: string; // check_unavailability_conflict
  os_schedules_site_shifts_id?: number;
  schedule_date?: string;
  us_user_site_profile_id?: string;
  department_code?: string;
  os_work_codes_id?: string;
  shift_color?: string;
  logs?: string;
  attendancetags?: any[];
}

// Create Open Shifts Request Parameters
export interface CreateOpenShiftsRequest {
  shifts: {
    shift_code: string;
    shift_date: string;
    positions: number;
    start_datetime: string;
    end_datetime: string;
  }[];
  employee_ids: string[];
  department_codes: string[];
  group_codes: string[];
  shift_attributes?: {
    notes?: string;
    site_code?: string;
    work_code?: string;
    shift_color?: string;
    department_code?: string;
    attendance_tags?: Array<{
      code: string;
      value: string | string[];
    }>;
  };
}

// Delete Schedule V3 Request Parameters
export interface DeleteScheduleV3Request {
  os_emp_schedules_id: number;
  os_emp_account_id: string;
}

// Schedule Audit Logs Request Parameters
export type ScheduleAuditAction =
  | 'create' | 'update' | 'delete' | 'publish' | 'publish_request' | 'copy' | 'mass_delete'
  | 'open_shift_assign' | 'open_shift_release' | 'open_shift_status'
  | 'swap_request' | 'swap_complete' | 'swap_status';

export type ScheduleAuditSource = 'web' | 'api' | 'cron';

export interface ScheduleAuditLogsRequest {
  start_date?: string;
  end_date?: string;
  os_emp_account_id?: string;
  os_emp_schedules_id?: string | number;
  include_related?: boolean;
  actor_id?: string;
  action?: ScheduleAuditAction | ScheduleAuditAction[];
  source?: ScheduleAuditSource;
  batch_id?: string;
  page?: number;
  limit?: number;
  order?: 'asc' | 'desc';
}

// Publish Schedule V3 Request Parameters
export type PublishScheduleScope = 'ALL' | 'SELF_CREATED' | 'EMPLOYEES' | 'DEPARTMENT' | 'SITE';

export interface PublishScheduleV3Request {
  start_date: string;
  end_date: string;
  scope: PublishScheduleScope;
  employee_ids?: number[];   // required if scope = EMPLOYEES
  department_codes?: string[];
  site_ids?: string[];
  publish_note?: string;
  send_notification: boolean;
  generate_ics: boolean;
}

// Publish Open Shift Request Parameters
export interface PublishOpenShiftRequest {
  start_date: string;
  end_date: string;
}

// Get Published Shifts for Assignment Request Parameters
export interface GetPublishedShiftsForAssignRequest {
  start_date: string;
  end_date: string;
}

// Assign Open Shift to Employee Request Parameters
export interface AssignOpenShiftToEmployeeRequest {
  shifts_data: Array<{
    selected_shifts: string | number;  // open_shift_id
    assigned_employees: string | number;  // employee_id
  }>;
}

// Copy Schedule Request Parameters
export type CopyScheduleSourceScope = 'all' | 'my' | 'department';
export type CopyScheduleSourceType = 'date_range' | 'current_view';
export type CopyScheduleViewType = 'daily' | 'weekly' | 'monthly';

export interface CopyScheduleCurrentViewFilter {
  /** Department codes */
  departments?: string[];
  /** Group codes/names */
  groups?: string[];
  /** Site ids */
  sites?: number[];
  /** Employee ids */
  emp_ids?: Array<string | number>;
}

export interface CopyScheduleOptions {
  clear_existing?: boolean;
  exclude_employee_ids?: Array<string | number>;
  include_departments?: string[];
}

export type CopyScheduleRequest =
  | {
      source: {
        source_type: 'date_range';
        scope?: CopyScheduleSourceScope;
        start_date: string;
        end_date: string;
      };
      destination: {
        start_date: string;
      };
      options?: CopyScheduleOptions;
    }
  | {
      source: {
        source_type: 'current_view';
        scope?: CopyScheduleSourceScope;
        view_type?: CopyScheduleViewType;
        start_date: string;
        end_date: string;
      };
      current_view_filter?: CopyScheduleCurrentViewFilter;
      destination: {
        start_date: string;
      };
      options?: CopyScheduleOptions;
    };

// Default values for request
export const DEFAULT_SCHEDULE_V3_FILTERS: ScheduleV3Filters = {
  department_codes: [],
  site_ids: [],
  employee_ids: [],
  group_code: [],
};

export const DEFAULT_SCHEDULE_V3_INCLUDE: ScheduleV3Include = {
  schedules: true,
  timeoff: false,
  holidays: true,
  unavailability: true,
  open_shifts: true,
};

// Helper function to build request body
export const buildScheduleV3Request = (
  params: GetSchedulesV3Params
): {
  start_date: string;
  end_date: string;
  group_by: SchedulesV3GroupByType;
  filters: ScheduleV3Filters;
  include: ScheduleV3Include;
  include_mydept?: IncludeDeptType;
} => {
  return {
    start_date: params.start_date,
    end_date: params.end_date,
    group_by: params.group_by,
    filters: params.filters || DEFAULT_SCHEDULE_V3_FILTERS,
    include: {
      ...DEFAULT_SCHEDULE_V3_INCLUDE,
      ...params.include,
    },
    ...(params.include_mydept !== undefined && { include_mydept: params.include_mydept }),
  };
};
